"use client"

import { useState, useEffect } from "react"
import { Settings } from "lucide-react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select"
import { Label } from "../ui/label"
import { Switch } from "../ui/switch"
import { Slider } from "../ui/slider"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs"
import { ScrollArea } from "../ui/scroll-area"

interface SettingsDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  toneOfVoice?: string
  onToneChange?: (tone: string) => void
  fontFamily?: string
  fontSize?: string
  onFontFamilyChange?: (family: string) => void
  onFontSizeChange?: (size: string) => void
  generateImages?: boolean
  imageStyle?: string
  imageGenre?: string
  onGenerateImagesChange?: (enabled: boolean) => void
  onImageStyleChange?: (style: string) => void
  onImageGenreChange?: (genre: string) => void
  screenshotRate?: number
  onScreenshotRateChange?: (fps: number) => void
  maxPatents?: number
  onMaxPatentsChange?: (patents: number) => void
  maxPriorArt?: number
  onMaxPriorArtChange?: (priorArt: number) => void
  maxSimilar?: number
  onMaxSimilarChange?: (similar: number) => void
  detailLevel?: number
  onDetailLevelChange?: (level: number) => void
  maxSources?: number
  onMaxSourcesChange?: (sources: number) => void
  navigationSpeed?: number
  onNavigationSpeedChange?: (speed: number) => void
  maxEcommerceSites?: number
  onMaxEcommerceSitesChange?: (sites: number) => void
  maxEcommerceResults?: number
  onMaxEcommerceResultsChange?: (results: number) => void
  rdpQuality?: "low" | "medium" | "high" | "ultra"
  onRdpQualityChange?: (quality: "low" | "medium" | "high" | "ultra") => void
  rdpFps?: number
  onRdpFpsChange?: (fps: number) => void
  rdpBufferSize?: number
  onRdpBufferSizeChange?: (size: number) => void
}

export function SettingsDialog({
  open: openProp,
  onOpenChange: onOpenChangeProp,
  toneOfVoice = "friendly",
  onToneChange,
  fontFamily = "Roboto",
  fontSize = "14",
  onFontFamilyChange,
  onFontSizeChange,
  generateImages = false,
  imageStyle = "realistic",
  imageGenre = "friendly",
  onGenerateImagesChange,
  onImageStyleChange,
  onImageGenreChange,
  screenshotRate = 10,
  onScreenshotRateChange,
  maxSources = 5,
  onMaxSourcesChange,
  maxPatents = 10,
  onMaxPatentsChange,
  maxPriorArt = 5,
  onMaxPriorArtChange,
  maxSimilar = 5,
  onMaxSimilarChange,
  detailLevel = 3,
  onDetailLevelChange,
  navigationSpeed = 4,
  onNavigationSpeedChange,
  maxEcommerceSites = 4,
  onMaxEcommerceSitesChange,
  maxEcommerceResults = 3,
  onMaxEcommerceResultsChange,
  rdpQuality = "medium",
  onRdpQualityChange,
  rdpFps = 10,
  onRdpFpsChange,
  rdpBufferSize = 3,
  onRdpBufferSizeChange,
}: SettingsDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = openProp !== undefined ? openProp : internalOpen
  const setOpen = onOpenChangeProp || setInternalOpen
  const [localTone, setLocalTone] = useState(toneOfVoice)
  const [localFontFamily, setLocalFontFamily] = useState(fontFamily)
  const [localFontSize, setLocalFontSize] = useState(fontSize)
  const [localGenerateImages, setLocalGenerateImages] = useState(generateImages)
  const [localImageStyle, setLocalImageStyle] = useState(imageStyle)
  const [localImageGenre, setLocalImageGenre] = useState(imageGenre)
  const [localScreenshotRate, setLocalScreenshotRate] = useState(screenshotRate)
  const [localMaxSources, setLocalMaxSources] = useState(maxSources)
  const [localMaxPatents, setLocalMaxPatents] = useState(maxPatents)
  const [localMaxPriorArt, setLocalMaxPriorArt] = useState(maxPriorArt)
  const [localMaxSimilar, setLocalMaxSimilar] = useState(maxSimilar)
  const [localDetailLevel, setLocalDetailLevel] = useState(detailLevel)
  const [localNavigationSpeed, setLocalNavigationSpeed] = useState(navigationSpeed)
  const [localMaxEcommerceSites, setLocalMaxEcommerceSites] = useState(maxEcommerceSites)
  const [localMaxEcommerceResults, setLocalMaxEcommerceResults] = useState(maxEcommerceResults)
  const [localRdpQuality, setLocalRdpQuality] = useState<"low" | "medium" | "high" | "ultra">(rdpQuality)
  const [localRdpFps, setLocalRdpFps] = useState(rdpFps)
  const [localRdpBufferSize, setLocalRdpBufferSize] = useState(rdpBufferSize)

  // Canvas appearance settings — safe server defaults, localStorage loaded in useEffect
  const [localCanvasSharpness, setLocalCanvasSharpness] = useState(100)
  const [localCanvasTint, setLocalCanvasTint] = useState(0)
  const [localCanvasTemperature, setLocalCanvasTemperature] = useState(0)
  const [localColorBlindness, setLocalColorBlindness] = useState("none")

  useEffect(() => {
    try {
      const sharpness = localStorage.getItem("AskFinz_canvas_sharpness")
      if (sharpness) setLocalCanvasSharpness(parseInt(sharpness, 10))
      const tint = localStorage.getItem("AskFinz_canvas_tint")
      if (tint) setLocalCanvasTint(parseInt(tint, 10))
      const temperature = localStorage.getItem("AskFinz_canvas_temperature")
      if (temperature) setLocalCanvasTemperature(parseInt(temperature, 10))
      const colorBlindness = localStorage.getItem("AskFinz_color_blindness")
      if (colorBlindness) setLocalColorBlindness(colorBlindness)
    } catch {}
  }, [])

  const handleSave = () => {
    onToneChange?.(localTone)
    onFontFamilyChange?.(localFontFamily)
    onFontSizeChange?.(localFontSize)
    onGenerateImagesChange?.(localGenerateImages)
    onImageStyleChange?.(localImageStyle)
    onImageGenreChange?.(localImageGenre)
    onScreenshotRateChange?.(localScreenshotRate)
    onMaxSourcesChange?.(localMaxSources)
    onMaxPatentsChange?.(localMaxPatents)
    onMaxPriorArtChange?.(localMaxPriorArt)
    onMaxSimilarChange?.(localMaxSimilar)
    onDetailLevelChange?.(localDetailLevel)
    onNavigationSpeedChange?.(localNavigationSpeed)
    onMaxEcommerceSitesChange?.(localMaxEcommerceSites)
    onMaxEcommerceResultsChange?.(localMaxEcommerceResults)
    onRdpQualityChange?.(localRdpQuality)
    onRdpFpsChange?.(localRdpFps)
    onRdpBufferSizeChange?.(localRdpBufferSize)
    // Save to localStorage immediately
    if (typeof window !== "undefined") {
      localStorage.setItem("AskFinz_tone_of_voice", localTone)
      localStorage.setItem("AskFinz_font_family", localFontFamily)
      localStorage.setItem("AskFinz_font_size", localFontSize)
      localStorage.setItem("AskFinz_generate_images", localGenerateImages.toString())
      localStorage.setItem("AskFinz_image_style", localImageStyle)
      localStorage.setItem("AskFinz_image_genre", localImageGenre)
      localStorage.setItem("AskFinz_screenshot_rate", localScreenshotRate.toString())
      localStorage.setItem("AskFinz_max_sources", localMaxSources.toString())
      localStorage.setItem("AskFinz_max_patents", localMaxPatents.toString())
      localStorage.setItem("AskFinz_max_prior_art", localMaxPriorArt.toString())
      localStorage.setItem("AskFinz_max_similar", localMaxSimilar.toString())
      localStorage.setItem("AskFinz_detail_level", localDetailLevel.toString())
      localStorage.setItem("AskFinz_navigation_speed", localNavigationSpeed.toString())
      localStorage.setItem("AskFinz_max_ecommerce_sites", localMaxEcommerceSites.toString())
      localStorage.setItem("AskFinz_max_ecommerce_results", localMaxEcommerceResults.toString())
      localStorage.setItem("AskFinz_canvas_sharpness", localCanvasSharpness.toString())
      localStorage.setItem("AskFinz_canvas_tint", localCanvasTint.toString())
      localStorage.setItem("AskFinz_canvas_temperature", localCanvasTemperature.toString())
      localStorage.setItem("AskFinz_color_blindness", localColorBlindness)
      localStorage.setItem("rdp_quality", localRdpQuality)
      localStorage.setItem("rdp_fps", localRdpFps.toString())
      localStorage.setItem("rdp_buffer_size", localRdpBufferSize.toString())

      // Dispatch custom event to notify other tabs/components of settings change
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("rdpSettingsChanged"))
      }
    }
    setOpen(false)
  }

  // Sync local state with props when dialog opens
  useEffect(() => {
    if (open) {
      setLocalTone(toneOfVoice)
      setLocalFontFamily(fontFamily)
      setLocalFontSize(fontSize)
      setLocalGenerateImages(generateImages)
      setLocalImageStyle(imageStyle)
      setLocalImageGenre(imageGenre)
      setLocalScreenshotRate(screenshotRate)
      setLocalMaxSources(maxSources)
      setLocalMaxPatents(maxPatents)
      setLocalMaxPriorArt(maxPriorArt)
      setLocalMaxSimilar(maxSimilar)
      setLocalDetailLevel(detailLevel)
      setLocalNavigationSpeed(navigationSpeed)
      setLocalMaxEcommerceSites(maxEcommerceSites)
      setLocalMaxEcommerceResults(maxEcommerceResults)

      // Load RDP settings from localStorage
      if (typeof window !== "undefined") {
        const savedQuality = localStorage.getItem("rdp_quality") as "low" | "medium" | "high" | "ultra" | null
        const savedFps = localStorage.getItem("rdp_fps")
        const savedBufferSize = localStorage.getItem("rdp_buffer_size")
        if (savedQuality) setLocalRdpQuality(savedQuality)
        if (savedFps) setLocalRdpFps(parseInt(savedFps, 10))
        if (savedBufferSize) setLocalRdpBufferSize(parseInt(savedBufferSize, 10))
      }

      // Load canvas appearance settings
      const sharpness = localStorage.getItem("AskFinz_canvas_sharpness")
      const tint = localStorage.getItem("AskFinz_canvas_tint")
      const temperature = localStorage.getItem("AskFinz_canvas_temperature")
      const blindness = localStorage.getItem("AskFinz_color_blindness")

      if (sharpness) setLocalCanvasSharpness(parseInt(sharpness, 10))
      if (tint) setLocalCanvasTint(parseInt(tint, 10))
      if (temperature) setLocalCanvasTemperature(parseInt(temperature, 10))
      if (blindness) setLocalColorBlindness(blindness)
    }
  }, [open, toneOfVoice, fontFamily, fontSize, generateImages, imageStyle, imageGenre, screenshotRate, maxSources, maxPatents, maxPriorArt, maxSimilar, detailLevel, navigationSpeed, maxEcommerceSites, maxEcommerceResults, rdpQuality, rdpFps, rdpBufferSize])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Only render trigger if dialog is uncontrolled (no open prop provided) */}
      {openProp === undefined && (
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2 sm:px-3"
            title="Settings"
          >
            <Settings className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Settings</span>
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription>
            Customize the assistant's response style and content.
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="general" className="flex-1 flex flex-col min-h-0 px-6">
          <TabsList className="grid w-full grid-cols-7 mb-4 flex-shrink-0">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="appearance">Appearance</TabsTrigger>
            <TabsTrigger value="images">Images</TabsTrigger>
            <TabsTrigger value="browser">Browser</TabsTrigger>
            <TabsTrigger value="patents">Patents</TabsTrigger>
            <TabsTrigger value="ecommerce">Ecommerce</TabsTrigger>
            <TabsTrigger value="rdp">RDP</TabsTrigger>
          </TabsList>
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-6 pb-4">
              <TabsContent value="general" className="space-y-4 mt-0">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="tone">Finn's Tone of Voice</Label>
                    <Select value={localTone} onValueChange={setLocalTone}>
                      <SelectTrigger id="tone" className="border-0">
                        <SelectValue placeholder="Select tone" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="supportive">Supportive</SelectItem>
                        <SelectItem value="simple">Simple</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="detailLevel">
                        Response Detail Level: {
                          localDetailLevel === 1 ? "Low" :
                            localDetailLevel === 2 ? "Medium-Low" :
                              localDetailLevel === 3 ? "Medium" :
                                localDetailLevel === 4 ? "Medium-High" :
                                  "High"
                        }
                      </Label>
                    </div>
                    <Slider
                      id="detailLevel"
                      min={1}
                      max={5}
                      step={1}
                      value={[localDetailLevel]}
                      onValueChange={(values) => setLocalDetailLevel(values[0])}
                      className="w-full"
                    />
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p><strong>1 - Low:</strong> Brief, concise answers with key points only</p>
                      <p><strong>2 - Medium-Low:</strong> Short answers with basic context</p>
                      <p><strong>3 - Medium:</strong> Balanced responses with moderate detail and examples</p>
                      <p><strong>4 - Medium-High:</strong> Detailed responses with comprehensive context and examples</p>
                      <p><strong>5 - High:</strong> Very detailed, in-depth responses with extensive context, examples, and analysis</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="appearance" className="space-y-4 mt-0">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="fontFamily">Font Family</Label>
                    <Select value={localFontFamily} onValueChange={setLocalFontFamily}>
                      <SelectTrigger id="fontFamily" className="border-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inter">Inter</SelectItem>
                        <SelectItem value="Roboto">Roboto</SelectItem>
                        <SelectItem value="Open Sans">Open Sans</SelectItem>
                        <SelectItem value="Lato">Lato</SelectItem>
                        <SelectItem value="Montserrat">Montserrat</SelectItem>
                        <SelectItem value="Poppins">Poppins</SelectItem>
                        <SelectItem value="Arial">Arial</SelectItem>
                        <SelectItem value="Georgia">Georgia</SelectItem>
                        <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="fontSize">Font Size (px)</Label>
                    <Input
                      id="fontSize"
                      type="number"
                      min="12"
                      max="24"
                      value={localFontSize}
                      onChange={(e) => setLocalFontSize(e.target.value)}
                    />
                  </div>

                  <div className="pt-4 mt-4">
                    <Label className="text-base font-semibold mb-4 block">Canvas Appearance</Label>

                    <div className="grid gap-4">
                      <div className="grid gap-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="canvasSharpness">
                            Sharpness: {localCanvasSharpness}%
                          </Label>
                        </div>
                        <Slider
                          id="canvasSharpness"
                          min={0}
                          max={200}
                          step={5}
                          value={[localCanvasSharpness]}
                          onValueChange={(values) => setLocalCanvasSharpness(values[0])}
                          className="w-full"
                        />
                      </div>

                      <div className="grid gap-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="canvasTint">
                            Tint: {localCanvasTint > 0 ? '+' : ''}{localCanvasTint}%
                          </Label>
                        </div>
                        <Slider
                          id="canvasTint"
                          min={-100}
                          max={100}
                          step={5}
                          value={[localCanvasTint]}
                          onValueChange={(values) => setLocalCanvasTint(values[0])}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground">
                          Negative values add green tint, positive values add magenta tint
                        </p>
                      </div>

                      <div className="grid gap-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="canvasTemperature">
                            Temperature: {localCanvasTemperature > 0 ? '+' : ''}{localCanvasTemperature}%
                          </Label>
                        </div>
                        <Slider
                          id="canvasTemperature"
                          min={-100}
                          max={100}
                          step={5}
                          value={[localCanvasTemperature]}
                          onValueChange={(values) => setLocalCanvasTemperature(values[0])}
                          className="w-full"
                        />
                        <p className="text-xs text-muted-foreground">
                          Negative values add blue (cool), positive values add orange (warm)
                        </p>
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="colorBlindness">Color Blindness Filter</Label>
                        <Select value={localColorBlindness} onValueChange={setLocalColorBlindness}>
                          <SelectTrigger id="colorBlindness" className="border-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None (Normal Vision)</SelectItem>
                            <SelectItem value="deuteranopia">Deuteranopia (No Green)</SelectItem>
                            <SelectItem value="protanopia">Protanopia (No Red)</SelectItem>
                            <SelectItem value="tritanopia">Tritanopia (No Blue)</SelectItem>
                            <SelectItem value="monochromacy">Monochromacy (No Colors)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Adjust canvas colors to simulate different types of color blindness
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="images" className="space-y-4 mt-0">
                <div className="grid gap-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="generateImages" className="cursor-pointer">
                      Generate Images for Responses
                    </Label>
                    <Switch
                      id="generateImages"
                      checked={localGenerateImages}
                      onCheckedChange={setLocalGenerateImages}
                    />
                  </div>
                  {localGenerateImages && (
                    <>
                      <div className="grid gap-2">
                        <Label htmlFor="imageStyle">Image Style</Label>
                        <Select value={localImageStyle} onValueChange={setLocalImageStyle}>
                          <SelectTrigger id="imageStyle" className="border-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="realistic">Realistic</SelectItem>
                            <SelectItem value="cartoon">Cartoon</SelectItem>
                            <SelectItem value="illustration">Illustration</SelectItem>
                            <SelectItem value="watercolor">Watercolor</SelectItem>
                            <SelectItem value="digital-art">Digital Art</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <Label htmlFor="imageGenre">Image Genre</Label>
                        <Select value={localImageGenre} onValueChange={setLocalImageGenre}>
                          <SelectTrigger id="imageGenre" className="border-0">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="medical">Medical</SelectItem>
                            <SelectItem value="friendly">Friendly</SelectItem>
                            <SelectItem value="educational">Educational</SelectItem>
                            <SelectItem value="comforting">Comforting</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="browser" className="space-y-4 mt-0">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="screenshotRate">
                        FPS (Frames Per Second): {localScreenshotRate}
                      </Label>
                    </div>
                    <Slider
                      id="screenshotRate"
                      min={10}
                      max={60}
                      step={5}
                      value={[localScreenshotRate]}
                      onValueChange={(values) => setLocalScreenshotRate(values[0])}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Lower values (10fps) reduce bandwidth, higher values (60fps) improve responsiveness
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="maxSources">
                        Web Research Sources: {localMaxSources}
                      </Label>
                    </div>
                    <Slider
                      id="maxSources"
                      min={5}
                      max={20}
                      step={1}
                      value={[localMaxSources]}
                      onValueChange={(values) => setLocalMaxSources(values[0])}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Number of sources to visit when researching topics (5-20). More sources provide more comprehensive information but take longer.
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="navigationSpeed">
                        Navigation Speed: {localNavigationSpeed} seconds per page
                      </Label>
                    </div>
                    <Slider
                      id="navigationSpeed"
                      min={1}
                      max={10}
                      step={0.5}
                      value={[localNavigationSpeed]}
                      onValueChange={(values) => setLocalNavigationSpeed(values[0])}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum time to wait per page during navigation (1-10 seconds). Lower values speed up navigation but may miss content that loads slowly. Default: 4 seconds.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="patents" className="space-y-4 mt-0">
                <div className="grid gap-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="grid gap-2">
                      <Label htmlFor="maxPatents" className="text-xs">
                        Patents to Analyze
                      </Label>
                      <Input
                        id="maxPatents"
                        type="number"
                        min="1"
                        max="50"
                        value={localMaxPatents}
                        onChange={(e) => setLocalMaxPatents(parseInt(e.target.value) || 10)}
                        className="h-9"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="maxPriorArt" className="text-xs">
                        Prior Art per Patent
                      </Label>
                      <Input
                        id="maxPriorArt"
                        type="number"
                        min="0"
                        max="20"
                        value={localMaxPriorArt}
                        onChange={(e) => setLocalMaxPriorArt(parseInt(e.target.value) || 5)}
                        className="h-9"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="maxSimilar" className="text-xs">
                        Similar per Patent
                      </Label>
                      <Input
                        id="maxSimilar"
                        type="number"
                        min="0"
                        max="20"
                        value={localMaxSimilar}
                        onChange={(e) => setLocalMaxSimilar(parseInt(e.target.value) || 5)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Configure how many patents to analyze from search results, and how many prior art and similar patents to extract for each analyzed patent.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="ecommerce" className="space-y-4 mt-0">
                <div className="grid gap-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid gap-2">
                      <Label htmlFor="maxEcommerceSites" className="text-xs">
                        Max Ecommerce Sites
                      </Label>
                      <Input
                        id="maxEcommerceSites"
                        type="number"
                        min="1"
                        max="10"
                        value={localMaxEcommerceSites}
                        onChange={(e) => setLocalMaxEcommerceSites(parseInt(e.target.value) || 4)}
                        className="h-9"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="maxEcommerceResults" className="text-xs">
                        Results per Site
                      </Label>
                      <Input
                        id="maxEcommerceResults"
                        type="number"
                        min="1"
                        max="10"
                        value={localMaxEcommerceResults}
                        onChange={(e) => setLocalMaxEcommerceResults(parseInt(e.target.value) || 3)}
                        className="h-9"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Configure how many ecommerce sites to search when looking for products, and how many product results to retrieve from each site. More sites and results provide more comprehensive price comparisons but take longer.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="rdp" className="space-y-4 mt-0">
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="rdpQuality">Quality Preset</Label>
                    <Select
                      value={localRdpQuality}
                      onValueChange={(value: "low" | "medium" | "high" | "ultra") => setLocalRdpQuality(value)}
                    >
                      <SelectTrigger id="rdpQuality" className="border-0">
                        <SelectValue placeholder="Select quality" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low (Fast, Lower Quality)</SelectItem>
                        <SelectItem value="medium">Medium (Balanced)</SelectItem>
                        <SelectItem value="high">High (Better Quality, Higher Latency)</SelectItem>
                        <SelectItem value="ultra">Ultra (Best Quality, Highest Latency)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {localRdpQuality === "low" && "Optimized for low latency and bandwidth. Lower image quality."}
                      {localRdpQuality === "medium" && "Balanced quality and performance. Recommended for most users."}
                      {localRdpQuality === "high" && "Higher image quality with increased latency and bandwidth usage."}
                      {localRdpQuality === "ultra" && "Maximum image quality. Best for high-speed connections."}
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="rdpFps">
                      Target FPS: {localRdpFps}
                    </Label>
                    <Slider
                      id="rdpFps"
                      min={5}
                      max={60}
                      step={1}
                      value={[localRdpFps]}
                      onValueChange={(value) => setLocalRdpFps(value[0])}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Higher FPS provides smoother motion but uses more bandwidth. Recommended: 10-15 FPS.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="rdpBufferSize">
                      Buffer Size: {localRdpBufferSize} frames
                    </Label>
                    <Slider
                      id="rdpBufferSize"
                      min={1}
                      max={10}
                      step={1}
                      value={[localRdpBufferSize]}
                      onValueChange={(value) => setLocalRdpBufferSize(value[0])}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Frame buffer size. Higher values reduce stuttering but increase latency. Recommended: 3-5 frames.
                    </p>
                  </div>
                </div>
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>
        <DialogFooter className="px-6 pb-6 pt-4 flex-shrink-0">
          <Button onClick={handleSave}>Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
